import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fetchYachts } from "../store/slices/yachtsSlice";
import { fetchYachtGroups } from "../store/slices/yachtGroupsSlice";
import { GlassCardImage, GlassCardBody } from "../components/ui/Glass";
import { Section, Container, SectionEyebrow } from "../components/ui/Section";
import { YachtCardSkeleton } from "../components/ui/Skeleton";
import { ITEMS_PER_PAGE } from "../components/ui/Pagination";
import CustomSelect from "../components/ui/CustomSelect";
import { ReactComponent as SearchIcon } from "../assets/icons/search.svg";
import {
  pickYachtCardImage,
  pickYachtHeroImage,
  cardImageFallback,
  mediaUrl,
} from "../utils/media";
import { formatCabins } from "../utils/format";
import {
  PageHeader,
  HeaderContent,
  Title,
  Subtitle,
  FiltersBar,
  FilterHeader,
  ResultCount,
  ClearBtn,
  Grid,
  YachtCard,
  PlaceholderImg,
  YachtBrand,
  YachtName,
  Specs,
  Spec,
  Price,
  EmptyState,
  ClearFiltersBtn,
  SearchField,
  ConditionCards,
  ConditionCard,
  ConditionCardImage,
  ConditionCardInner,
  ConditionCardTop,
  ConditionCardCount,
  ConditionCardBody,
  ConditionCardTitle,
  ConditionCardDesc,
  ConditionCardCTA,
  BackLink,
  Sentinel,
  GroupSection,
  GroupHeader,
  GroupHeading,
  GroupTitle,
  GroupSubtitle,
  GroupCount,
} from "./styles/YachtsPage.styles";

const CONDITIONS = [
  {
    value: "availableNow",
    label: "Available Now",
    description: "Yachts in our inventory, ready for immediate handover.",
    variant: "success",
  },
  {
    value: "brokerage",
    label: "Brokerage",
    description: "Curated pre-owned vessels offered through brokerage.",
    variant: "primary",
  },
];

// Third card is no longer a yacht filter; it links to the New Buildings page.
const NEW_BUILDINGS_CARD = {
  label: "New Buildings",
  description:
    "Yacht building management, commissioned to your specification and supervised from first sketch to delivery.",
  variant: "gold",
  to: "/new-buildings",
  cta: "Discover",
  topLabel: "Build to order",
  image:
    "/images/princess/X95/exterior/large/exterior-01_x95-slot-5-exterior-white-hull-6.webp",
};

const TYPES = ["All Types", "Motor Yacht", "Sailing Yacht", "Catamaran"];
const LENGTHS = [
  "Any Length",
  "Under 15m",
  "15m - 20m",
  "20m - 25m",
  "Over 25m",
];
const SORT_OPTIONS = [
  "Price: Low to High",
  "Price: High to Low",
  "Newest First",
  "Length: Largest",
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
  exit: { opacity: 0, scale: 0.96, transition: { duration: 0.25 } },
};

function formatPrice(num) {
  if (num == null) return "Contact for price";
  return `€${Number(num).toLocaleString("de-DE")}`;
}

const pickCardImage = pickYachtCardImage;

const STATIC_CONDITION_IMAGES = {
  availableNow:
    "/images/princess/Y80/exterior/large/exterior-04_y80-exterior-flybridge-1.webp",
};

function YachtsPage() {
  const dispatch = useDispatch();
  const { items: yachts, loading } = useSelector((state) => state.yachts);
  const { items: yachtGroups } = useSelector((state) => state.yachtGroups);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [condition, setCondition] = useState(
    searchParams.get("condition") || "all",
  );
  const [type, setType] = useState("All Types");
  const [brand, setBrand] = useState(searchParams.get("brand") || "All Brands");
  const [length, setLength] = useState("Any Length");
  const [sort, setSort] = useState("Price: Low to High");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleConditionChange = (next) => {
    setCondition(next);
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("condition");
    else params.set("condition", next);
    params.delete("page");
    // Push (not replace) so the browser Back button returns to the overview
    // and clears the condition param naturally.
    setSearchParams(params);
  };

  // Keep the condition in sync with the URL so browser Back/Forward updates
  // the view (e.g. leaving the Available Now grouping).
  useEffect(() => {
    setCondition(searchParams.get("condition") || "all");
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchYachtGroups());
  }, [dispatch]);

  // "Upon Request" is retired as a public filter; redirect any old links
  // (e.g. /yachts?condition=uponRequest) to the New Buildings page.
  useEffect(() => {
    if (condition === "uponRequest") {
      navigate("/new-buildings", { replace: true });
    }
  }, [condition, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const params = { category: "yacht" };
    if (debouncedSearch) params.search = debouncedSearch;
    dispatch(fetchYachts(params));
    if (searchParams.get("page")) {
      const next = new URLSearchParams(searchParams);
      next.delete("page");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, debouncedSearch]);

  const BRANDS = useMemo(() => {
    const brandNames = yachts.map((y) => y.brand?.name).filter(Boolean);
    return ["All Brands", ...new Set(brandNames)];
  }, [yachts]);

  // List filtered by everything except condition, used for per-condition counts
  const baseFiltered = useMemo(() => {
    let list = [...yachts];

    if (type !== "All Types") list = list.filter((y) => y.type === type);
    if (brand !== "All Brands")
      list = list.filter((y) => y.brand?.name === brand);
    if (length !== "Any Length") {
      if (length === "Under 15m") list = list.filter((y) => y.length < 15);
      else if (length === "15m - 20m")
        list = list.filter((y) => y.length >= 15 && y.length <= 20);
      else if (length === "20m - 25m")
        list = list.filter((y) => y.length > 20 && y.length <= 25);
      else if (length === "Over 25m") list = list.filter((y) => y.length > 25);
    }
    return list;
  }, [yachts, type, brand, length]);

  const conditionCounts = useMemo(() => {
    const counts = {
      all: baseFiltered.length,
      availableNow: 0,
      brokerage: 0,
      uponRequest: 0,
    };
    for (const y of baseFiltered) {
      if (counts[y.condition] != null) counts[y.condition] += 1;
    }
    // "Available Now" is shown as curated groups (see availableNowSections),
    // not the raw condition list, so its card must count the distinct yachts
    // actually displayed in those sections rather than every availableNow yacht.
    const availableNowIds = new Set();
    for (const g of yachtGroups) {
      for (const y of Array.isArray(g.yachts) ? g.yachts : []) {
        availableNowIds.add(y.id);
      }
    }
    counts.availableNow = availableNowIds.size;
    return counts;
  }, [baseFiltered, yachtGroups]);

  const conditionImages = useMemo(() => {
    const map = { availableNow: null, brokerage: null, uponRequest: null };
    for (const y of yachts) {
      if (map[y.condition] == null) {
        const img = pickYachtHeroImage(y);
        if (img) map[y.condition] = img;
      }
    }
    for (const key of Object.keys(STATIC_CONDITION_IMAGES)) {
      if (STATIC_CONDITION_IMAGES[key]) {
        map[key] = mediaUrl(STATIC_CONDITION_IMAGES[key]);
      }
    }
    return map;
  }, [yachts]);

  const filtered = useMemo(() => {
    let list = [...baseFiltered];

    if (condition !== "all")
      list = list.filter((y) => y.condition === condition);

    const nullsLast = (a, b) => (a == null) - (b == null);
    if (sort === "Price: Low to High")
      list.sort(
        (a, b) =>
          nullsLast(a.price, b.price) || (a.price ?? 0) - (b.price ?? 0),
      );
    else if (sort === "Price: High to Low")
      list.sort(
        (a, b) =>
          nullsLast(a.price, b.price) || (b.price ?? 0) - (a.price ?? 0),
      );
    else if (sort === "Newest First")
      list.sort(
        (a, b) => nullsLast(a.year, b.year) || (b.year ?? 0) - (a.year ?? 0),
      );
    else if (sort === "Length: Largest")
      list.sort((a, b) => (b.length ?? 0) - (a.length ?? 0));

    return list;
  }, [baseFiltered, condition, sort]);

  // Curated "Available Now" sections, admin-ordered groups, each with its
  // yachts. Built from the /yacht-groups payload (which already nests each
  // group's available yachts in admin order) so it includes tenders and other
  // brand categories that the category-filtered /yachts list leaves out.
  const availableNowSections = useMemo(() => {
    return [...yachtGroups]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((g) => ({
        id: g.id,
        title: g.title,
        subtitle: g.subtitle,
        yachts: Array.isArray(g.yachts) ? g.yachts : [],
      }))
      .filter((s) => s.yachts.length > 0);
  }, [yachtGroups]);

  const isGroupedView = condition === "availableNow";

  // Infinite scroll for the flat (non-grouped) list: reveal more as the user
  // scrolls instead of paginating. All yachts are already in memory, so this
  // is purely client-side slicing.
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const sentinelRef = useRef(null);

  // Reset the visible window whenever the result set changes.
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [condition, type, brand, length, sort, debouncedSearch]);

  const visibleYachts = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount],
  );

  useEffect(() => {
    if (isGroupedView || visibleCount >= filtered.length) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(c + ITEMS_PER_PAGE, filtered.length));
        }
      },
      { rootMargin: "400px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isGroupedView, filtered.length, visibleCount]);

  const hasFilters =
    condition !== "all" ||
    type !== "All Types" ||
    brand !== "All Brands" ||
    length !== "Any Length" ||
    searchInput !== "";

  const clearFilters = () => {
    handleConditionChange("all");
    setType("All Types");
    setBrand("All Brands");
    setLength("Any Length");
    setSearchInput("");
  };

  const renderYachtCard = (yacht) => (
    <motion.div
      key={yacht.id}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{ height: "100%" }}
    >
      <Link
        to={`/yachts/${yacht.id}`}
        style={{ display: "block", height: "100%" }}
      >
        <YachtCard>
          <GlassCardImage $ratio="16 / 10">
            {pickCardImage(yacht) ? (
              <img
                src={pickCardImage(yacht)}
                alt={yacht.name}
                loading="lazy"
                decoding="async"
                onError={cardImageFallback(yacht)}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <PlaceholderImg>Photo coming soon</PlaceholderImg>
            )}
          </GlassCardImage>

          <GlassCardBody>
            <YachtBrand>{yacht.brand?.name}</YachtBrand>
            <YachtName>{yacht.name}</YachtName>
            <Specs>
              <Spec>
                <span>{yacht.subtype ? "Class" : "Year"}</span>
                <p>{yacht.subtype || yacht.year || "—"}</p>
              </Spec>
              <Spec>
                <span>Length</span>
                <p>{yacht.length ? `${yacht.length}m` : "—"}</p>
              </Spec>
              {formatCabins(yacht.cabins) && (
                <Spec $truncate>
                  <span>Cabins</span>
                  <p title={yacht.cabins}>{formatCabins(yacht.cabins)}</p>
                </Spec>
              )}
              <Price>{formatPrice(yacht.price)}</Price>
            </Specs>
          </GlassCardBody>
        </YachtCard>
      </Link>
    </motion.div>
  );

  return (
    <>
      <PageHeader>
        <Container>
          <SectionEyebrow>Our Fleet</SectionEyebrow>
          <HeaderContent>
            <Title>Explore Yachts</Title>
            <Subtitle>
              Browse our curated collection of premium vessels from the world's
              leading shipyards.
            </Subtitle>
          </HeaderContent>
        </Container>
      </PageHeader>

      {condition === "all" && (
        <Section $bg="offWhite" $py="3rem">
          <Container>
            <ConditionCards role="tablist" aria-label="Yacht acquisition type">
              {CONDITIONS.map((c) => (
                <ConditionCard
                  key={c.value}
                  type="button"
                  role="tab"
                  aria-selected={false}
                  $variant={c.variant}
                  onClick={() => handleConditionChange(c.value)}
                >
                  <ConditionCardImage
                    $src={conditionImages[c.value]}
                    $variant={c.variant}
                  />
                  <ConditionCardInner $variant={c.variant}>
                    <ConditionCardTop>
                      <ConditionCardCount>
                        <strong>{conditionCounts[c.value] ?? 0}</strong>{" "}
                        {conditionCounts[c.value] === 1 ? "yacht" : "yachts"}
                      </ConditionCardCount>
                    </ConditionCardTop>
                    <ConditionCardBody>
                      <ConditionCardTitle>{c.label}</ConditionCardTitle>
                      <ConditionCardDesc>{c.description}</ConditionCardDesc>
                    </ConditionCardBody>
                    <ConditionCardCTA>View yachts</ConditionCardCTA>
                  </ConditionCardInner>
                </ConditionCard>
              ))}

              <ConditionCard
                as={Link}
                to={NEW_BUILDINGS_CARD.to}
                $variant={NEW_BUILDINGS_CARD.variant}
              >
                <ConditionCardImage
                  $src={mediaUrl(NEW_BUILDINGS_CARD.image)}
                  $variant={NEW_BUILDINGS_CARD.variant}
                />
                <ConditionCardInner $variant={NEW_BUILDINGS_CARD.variant}>
                  <ConditionCardTop>
                    <ConditionCardCount>
                      {NEW_BUILDINGS_CARD.topLabel}
                    </ConditionCardCount>
                  </ConditionCardTop>
                  <ConditionCardBody>
                    <ConditionCardTitle>
                      {NEW_BUILDINGS_CARD.label}
                    </ConditionCardTitle>
                    <ConditionCardDesc>
                      {NEW_BUILDINGS_CARD.description}
                    </ConditionCardDesc>
                  </ConditionCardBody>
                  <ConditionCardCTA>{NEW_BUILDINGS_CARD.cta}</ConditionCardCTA>
                </ConditionCardInner>
              </ConditionCard>
            </ConditionCards>
          </Container>
        </Section>
      )}

      {condition !== "all" && (
        <>
          {!isGroupedView && (
            <Section $bg="offWhite" $py="1.5rem">
              <Container>
                <FilterHeader>
                  <BackLink as={Link} to="/yachts">
                    All yachts
                  </BackLink>
                  <ResultCount>
                    <span>{filtered.length}</span>{" "}
                    {filtered.length === 1 ? "yacht" : "yachts"} found
                  </ResultCount>
                </FilterHeader>
                <FiltersBar>
                  <SearchField>
                    <SearchIcon />
                    <input
                      type="text"
                      placeholder="Search yachts..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      aria-label="Search yachts"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        onClick={() => setSearchInput("")}
                        aria-label="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </SearchField>
                  <CustomSelect
                    value={type}
                    options={TYPES}
                    onChange={setType}
                  />
                  <CustomSelect
                    value={brand}
                    options={BRANDS}
                    onChange={setBrand}
                  />
                  <CustomSelect
                    value={length}
                    options={LENGTHS}
                    onChange={setLength}
                  />
                  <CustomSelect
                    value={sort}
                    options={SORT_OPTIONS}
                    onChange={setSort}
                  />
                  {hasFilters && (
                    <ClearBtn onClick={clearFilters}>Clear filters</ClearBtn>
                  )}
                </FiltersBar>
              </Container>
            </Section>
          )}

          <Section $bg="offWhite" $py="2rem">
            <Container>
              {isGroupedView && (
                <BackLink as={Link} to="/yachts">
                  All yachts
                </BackLink>
              )}
              <div style={{ scrollMarginTop: "100px" }}>
                {loading ? (
                  <Grid>
                    {Array.from({ length: 9 }).map((_, i) => (
                      <YachtCardSkeleton key={i} $light />
                    ))}
                  </Grid>
                ) : isGroupedView ? (
                  availableNowSections.length === 0 ? (
                    <Grid>
                      <EmptyState>
                        <SearchIcon />
                        <p>No yachts available</p>
                        <span>
                          Our fleet is being updated. Check back soon.
                        </span>
                      </EmptyState>
                    </Grid>
                  ) : (
                    availableNowSections.map((section) => (
                      <GroupSection key={section.id}>
                        <GroupHeader>
                          <GroupHeading>
                            <GroupTitle>{section.title}</GroupTitle>
                            {section.subtitle && (
                              <GroupSubtitle>{section.subtitle}</GroupSubtitle>
                            )}
                          </GroupHeading>
                          <GroupCount>
                            {section.yachts.length}{" "}
                            {section.yachts.length === 1 ? "yacht" : "yachts"}
                          </GroupCount>
                        </GroupHeader>
                        <Grid>{section.yachts.map(renderYachtCard)}</Grid>
                      </GroupSection>
                    ))
                  )
                ) : filtered.length === 0 ? (
                  <Grid>
                    <EmptyState>
                      <SearchIcon />
                      {hasFilters ? (
                        <>
                          <p>No yachts match your filters</p>
                          <span>
                            Try adjusting or clearing filters to see more
                            results.
                          </span>
                          <ClearFiltersBtn onClick={clearFilters}>
                            Clear All Filters
                          </ClearFiltersBtn>
                        </>
                      ) : (
                        <>
                          <p>No yachts available</p>
                          <span>
                            Our fleet is being updated. Check back soon.
                          </span>
                        </>
                      )}
                    </EmptyState>
                  </Grid>
                ) : (
                  <>
                    <Grid>
                      <AnimatePresence mode="popLayout">
                        {visibleYachts.map(renderYachtCard)}
                      </AnimatePresence>
                    </Grid>
                    {visibleCount < filtered.length && (
                      <Sentinel ref={sentinelRef} aria-hidden="true" />
                    )}
                  </>
                )}
              </div>
            </Container>
          </Section>
        </>
      )}
    </>
  );
}

export default YachtsPage;
